import { useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { Card } from '~/eds/src/components/Card/Card';
import { Select } from '~/eds/src/components/Select/Select';
import { Text } from '~/eds/src/components/Text/Text';
import {
  WorkflowIntegrationType,
  type WorkflowIntegrationTypeValue,
} from '~/enums/WorkflowIntegrationType';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import FlexLayout from '~/ui/components/FlexLayout/FlexLayout';
import Switch from '~/ui/components/Switch/Switch';

const messages = defineMessages({
  workdayProcurementTitle: {
    id: 'Setup.workdayProcurementTitle',
    defaultMessage: 'Workday Procurement',
    description: 'Title for workday procurement integration',
  },
  strategicSourcingTitle: {
    id: 'Setup.strategicSourcingTitle',
    defaultMessage: 'Strategic Sourcing',
    description: 'Title for strategic sourcing integration',
  },
  workdayProcurementDescription: {
    id: 'Setup.workdayProcurementDescription',
    defaultMessage:
      'Create new contracts with this workflow to send them to Procurement.',
    description: 'Description for workday procurement integration',
  },
  workdayProcurementIntegrationTypeSelectorDescription: {
    id: 'Setup.workdayProcurementIntegrationTypeSelectorDescription',
    defaultMessage:
      'Create and send contracts to Procurement. These workflows include Procurement fields.',
    description:
      'Description when integration type selector is enabled for Workday Procurement',
  },
  strategicSourcingDescription: {
    id: 'Setup.strategicSourcingDescription',
    defaultMessage:
      'Create new contracts with this workflow and link data fields. Once connected, only Strategic Sourcing can use this workflow.',
    description: 'Description for strategic sourcing integration',
  },
  integrationTypePlaceholder: {
    id: 'Setup.selectIntegrationTypePlaceholder',
    defaultMessage: 'Select integration type...',
    description: 'Placeholder for workflow integration type dropdown',
  },
  integrationTypeProcurement: {
    id: 'Setup.integrationTypeProcurement',
    defaultMessage: 'Procurement',
    description: 'Label for WorkflowIntegrationType.PROCUREMENT',
  },
  integrationTypeWssOnly: {
    id: 'Setup.integrationTypeWssOnly',
    defaultMessage: 'Workday Strategic Sourcing',
    description: 'Label for WorkflowIntegrationType.WSS_ONLY',
  },
});

export type WSSDesignatedIntegrationCardProps = {
  disabled: boolean;
  integrationType: WorkflowIntegrationTypeValue;
  onChange: (value: boolean) => void;
  onIntegrationTypeChange: (value: WorkflowIntegrationTypeValue) => void;
  isWssDesignated: boolean;
};

type SelectContractIntegrationTypeCardProps = {
  integrationType: WorkflowIntegrationTypeValue;
  onIntegrationTypeChange: (value: WorkflowIntegrationTypeValue) => void;
};

function SelectContractIntegrationTypeCard({
  integrationType,
  onIntegrationTypeChange,
}: SelectContractIntegrationTypeCardProps) {
  const intl = useIntl();

  const dropdownOptions = useMemo(
    () => [
      {
        value: WorkflowIntegrationType.PROCUREMENT,
        label: intl.formatMessage(messages.integrationTypeProcurement),
      },
      {
        value: WorkflowIntegrationType.WSS_ONLY,
        label: intl.formatMessage(messages.integrationTypeWssOnly),
      },
    ],
    [intl],
  );

  if (integrationType === WorkflowIntegrationType.NONE) return null;

  return (
    <FlexLayout flexDirection="column" space={2} sx={{ width: '100%' }}>
      <Select<WorkflowIntegrationTypeValue, false>
        isMulti={false}
        name="workflow-setup-integration-type"
        options={dropdownOptions}
        value={integrationType}
        width="100%"
        enableErrorMessage={false}
        onChange={(value) => {
          if (!value) return;
          onIntegrationTypeChange(value);
        }}
      />
    </FlexLayout>
  );
}

export function SelectWorkdayProcurementIntegrationType({
  disabled,
  integrationType,
  onChange,
  onIntegrationTypeChange,
  isWssDesignated,
}: WSSDesignatedIntegrationCardProps) {
  const intl = useIntl();
  const isWorkdayProcurementCopy = useFlag(FlagType.WSSIntegrationPhase2M1M3);
  const canSelectIntegrationType = useFlag(
    FlagType.WSSIntegrationWorkflowBuilderIntegrationTypeSelector,
  );

  const descriptionMsg = (() => {
    if (!isWorkdayProcurementCopy) {
      return messages.strategicSourcingDescription;
    }
    if (canSelectIntegrationType) {
      return messages.workdayProcurementIntegrationTypeSelectorDescription;
    }
    return messages.workdayProcurementDescription;
  })();

  return (
    <Card disabled={disabled}>
      <FlexLayout flexDirection="column" space={3}>
        <FlexLayout alignItems="center" justifyContent="space-between">
          <FlexLayout
            flexDirection="column"
            space={2}
            sx={{ maxWidth: '600px' }}
          >
            <Text color="text.primary" variant="body-bold">
              {isWorkdayProcurementCopy
                ? intl.formatMessage(messages.workdayProcurementTitle)
                : intl.formatMessage(messages.strategicSourcingTitle)}
            </Text>
            <Text color="text.secondary" variant="body">
              {intl.formatMessage(descriptionMsg)}
            </Text>
          </FlexLayout>
          <Switch
            id="workflow--set-up-wss-designation"
            value={isWssDesignated}
            onChange={onChange}
          />
        </FlexLayout>
        {canSelectIntegrationType && (
          <SelectContractIntegrationTypeCard
            integrationType={integrationType}
            onIntegrationTypeChange={onIntegrationTypeChange}
          />
        )}
      </FlexLayout>
    </Card>
  );
}
